'use client'
import { getLanguageSwitchedUrl, type Language } from '@/lib/blog-language-mapping'
import { cn } from '@/lib/utils'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown, X } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { Button } from './ui/button'

type NavigationItem = {
  name: string
  href: string
  children?: NavigationItem[]
}

interface Props {
  navigation: NavigationItem[]
  setMobileMenuOpen: (open: boolean) => void
  locale: string
  pathname: string
  dictionary: Dictionary
}

export default function MobileNavbar({ setMobileMenuOpen, navigation, locale, pathname, dictionary }: Props) {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)

  const toggleDropdown = (name: string) => {
    setActiveDropdown(activeDropdown === name ? null : name)
  }

  // Language options for the dropdown
  const languages = [
    { code: 'en', name: 'EN' },
    { code: 'tr', name: 'TR' },
  ]

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className='fixed inset-0 z-50 flex h-screen items-center justify-center lg:hidden'
      onClick={() => setMobileMenuOpen(false)}
      role='dialog'
      aria-modal='true'
      aria-label='Mobile navigation menu'
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className='relative h-screen w-full overflow-y-auto rounded-xl bg-gradient-to-br from-slate-950 to-blue-950 p-6 shadow-2xl text-white'
        onClick={e => e.stopPropagation()}
        role='navigation'
        aria-label='Mobile menu'
      >
        <div className='flex items-center justify-between'>
          <Link
            href={`/${locale}`}
            className='-m-1.5 p-1.5'
            onClick={() => setMobileMenuOpen(false)}
          >
            <span className='sr-only'>{dictionary.common.logo.srOnly}</span>
            <Image
              className='h-8 w-auto'
              src={'/assets/images/logo/logo.png'}
              alt={dictionary.common.logo.altText}
              width={150}
              height={32}
            />
          </Link>
          <button
            type='button'
            className='rounded-full p-2.5 text-slate-200 transition-colors hover:bg-slate-800'
            onClick={() => setMobileMenuOpen(false)}
          >
            <span className='sr-only'>{dictionary.common.navigation.closeMenu}</span>
            <X
              className='h-6 w-6'
              aria-hidden='true'
            />
          </button>
        </div>

        <div className='mt-6 flow-root'>
          <div className='-my-6 divide-y divide-slate-700'>
            <div className='space-y-1 py-6'>
              {navigation.map(item => (
                <div key={item.name}>
                  {item.children ? (
                    <div className='mb-2'>
                      <Button
                        variant={'ghost'}
                        size={'lg'}
                        className='flex w-full items-center justify-between rounded-md px-3 py-2 text-base font-medium text-white transition-colors hover:bg-slate-800'
                        onClick={() => toggleDropdown(item.name)}
                        aria-expanded={activeDropdown === item.name}
                        aria-haspopup='true'
                        aria-controls={`mobile-${item.name}-menu`}
                      >
                        {item.name}
                        <ChevronDown
                          className={cn('h-5 w-5 text-slate-400 transition-transform duration-200', activeDropdown === item.name ? 'rotate-180' : '')}
                          aria-hidden='true'
                        />
                      </Button>

                      <AnimatePresence>
                        {activeDropdown === item.name && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className='overflow-hidden'
                            id={`mobile-${item.name}-menu`}
                            role='menu'
                            aria-orientation='vertical'
                            aria-labelledby={`mobile-${item.name}-button`}
                          >
                            <div className='mt-1 space-y-1 pl-4'>
                              {item.children.map(subItem => (
                                <Link
                                  key={subItem.name}
                                  href={subItem.href}
                                  className='block rounded-md px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-800 hover:text-white'
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  {subItem.name}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      href={item.href}
                      className='block rounded-md px-3 py-2 text-base font-medium text-white transition-colors hover:bg-slate-800'
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            <div className='py-6'>
              <div className='mb-2'>
                <Button
                  variant='ghost'
                  size='lg'
                  className='flex items-center justify-between rounded-md px-3 py-2 text-base font-medium text-white transition-colors hover:bg-slate-800'
                  onClick={() => toggleDropdown('language')}
                  aria-expanded={activeDropdown === 'language'}
                >
                  {locale === 'en' ? 'EN' : 'TR'}
                  <ChevronDown className={cn('h-5 w-5 text-slate-400 transition-transform duration-200', activeDropdown === 'language' ? 'rotate-180' : '')} />
                </Button>

                <AnimatePresence>
                  {activeDropdown === 'language' && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className='overflow-hidden'
                    >
                      <div className='mt-1 space-y-1 pl-4'>
                        {languages.map(lang => (
                          <Link
                            key={lang.code}
                            href={getLanguageSwitchedUrl(pathname, locale as Language, lang.code as Language)}
                            className={cn(
                              'block rounded-md px-3 py-2 text-sm font-medium transition-colors',
                              locale === lang.code ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                            )}
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {lang.name}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}
